// src/services/BillingItemsService.js

import API from './api';

const BillingItemsService = {
    // Get all billing items
    getAllBillingItems: async () => {
        try {
            const res = await API.get('/api/billing-items');
            return res.data;
        } catch (error) {
            console.error('Error getting all billing items:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get billing item by ID
    getBillingItemById: async (billingItemId) => {
        try {
            const res = await API.get(`/api/billing-items/${billingItemId}`);
            return res.data;
        } catch (error) {
            console.error('Error getting billing item:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get billing items by client ID
    getBillingItemsByClient: async (clientId) => {
        try {
            const res = await API.get(`/api/billing-items/client/${clientId}`);
            return res.data;
        } catch (error) {
            console.error('Error getting client billing items:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get billing items by project ID
    getBillingItemsByProject: async (projectId) => {
        try {
            const res = await API.get(`/api/billing-items/project/${projectId}`);
            return res.data;
        } catch (error) {
            console.error('Error getting project billing items:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get billing items by status
    getBillingItemsByStatus: async (status) => {
        try {
            const res = await API.get(`/api/billing-items/status/${status}`);
            return res.data;
        } catch (error) {
            console.error('Error getting billing items by status:', error.response?.data || error.message);
            throw error;
        }
    },

    // Create new billing item
    createBillingItem: async (billingItemData) => {
        try {
            console.log("createBillingItem started");
            const res = await API.post('/api/billing-items', billingItemData);
            console.log("createBillingItem", res.data);
            return res.data;
        } catch (error) {
            console.error('Error creating billing item:', error.response?.data || error.message);
            throw error;
        }
    },

    // Update billing item
    updateBillingItem: async (billingItemId, billingItemData) => {
        try {
            const res = await API.put(`/api/billing-items/${billingItemId}`, billingItemData);
            return res.data;
        } catch (error) {
            console.error('Error updating billing item:', error.response?.data || error.message);
            throw error;
        }
    },

    // Add payment to billing item
    addPayment: async (billingItemId, paymentData) => {
        try {
            const res = await API.patch(`/api/billing-items/${billingItemId}/payment`, paymentData);
            return res.data;
        } catch (error) {
            console.error('Error adding payment:', error.response?.data || error.message);
            throw error;
        }
    },

    // Delete billing item
    deleteBillingItem: async (billingItemId) => {
        try {
            const res = await API.delete(`/api/billing-items/${billingItemId}`);
            return res.data;
        } catch (error) {
            console.error('Error deleting billing item:', error.response?.data || error.message);
            throw error;
        }
    },

    // Update overdue billing items
    updateOverdueBillingItems: async () => {
        try {
            const res = await API.patch('/api/billing-items/update-overdue');
            return res.data;
        } catch (error) {
            console.error('Error updating overdue billing items:', error.response?.data || error.message);
            throw error;
        }
    },

    // Calculate total unpaid amount for a client
    getTotalUnpaidForClient: async (clientId) => {
        try {
            // First get all client's billing items
            const clientBillingItems = await BillingItemsService.getBillingItemsByClient(clientId);
            
            // Filter for unpaid/overdue items and calculate total
            const unpaidItems = clientBillingItems.filter(
                item => item.status === 'PENDING' || item.status === 'OVERDUE'
            );
            
            const totalUnpaid = unpaidItems.reduce((total, item) => {
                // Calculate what's been paid already
                const paidAmount = item.paidAmount.reduce(
                    (paidTotal, payment) => paidTotal + payment.amount, 
                    0
                );
                // Add remaining amount to total
                return total + (item.amount - paidAmount);
            }, 0);
            
            return totalUnpaid;
        } catch (error) {
            console.error('Error calculating unpaid total:', error.response?.data || error.message);
            throw error;
        }
    },
    
    // Get payment history for a client
    getClientPaymentHistory: async (clientId) => {
        try {
            // Get all client's billing items
            const clientBillingItems = await BillingItemsService.getBillingItemsByClient(clientId);
            
            // Extract payment history
            const paymentHistory = [];
            
            clientBillingItems.forEach(item => {
                item.paidAmount.forEach(payment => {
                    paymentHistory.push({
                        billingItemId: item._id,
                        billingItemTitle: item.title,
                        description: payment.description,
                        amount: payment.amount,
                        paidDate: payment.paidDate,
                        projectId: item.project?._id,
                        projectName: item.project?.name
                    });
                });
            });
            
            // Sort by date descending (newest first)
            return paymentHistory.sort((a, b) => 
                new Date(b.paidDate) - new Date(a.paidDate)
            );
        } catch (error) {
            console.error('Error getting payment history:', error.response?.data || error.message);
            throw error;
        }
    }
};

export default BillingItemsService;